import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#applyAltColorFilter(this.dataset.selectedColor);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (source) {
      const newMediaGallery = source.querySelector('media-gallery');

      if (newMediaGallery) {
        this.replaceWith(newMediaGallery);
        return;
      }
    }

    if (this.dataset.altColorFilter !== 'true') return;

    const variant = event.detail.resource;
    if (variant?.featured_media?.id != null) {
      this.dataset.variantFeaturedMediaId = String(variant.featured_media.id);
    }

    const colorName = this.#getColorFromVariant(variant) || this.dataset.selectedColor;
    this.#applyAltColorFilter(colorName);
  };

  /**
   * @param {object | null | undefined} variant
   * @returns {string | undefined}
   */
  #getColorFromVariant(variant) {
    if (!variant) return undefined;

    const optionIndex = Number(this.dataset.colorOptionIndex);
    if (optionIndex === 1) return variant.option1;
    if (optionIndex === 2) return variant.option2;
    if (optionIndex === 3) return variant.option3;

    return variant.option2 || variant.option1;
  }

  /**
   * Show only gallery items whose image alt text exactly matches the selected colour.
   * @param {string | undefined} colorName
   */
  #applyAltColorFilter(colorName) {
    if (this.dataset.altColorFilter !== 'true' || !colorName) return;

    const normalizedColor = colorName.trim().toLowerCase();
    if (!normalizedColor) return;

    this.dataset.selectedColor = colorName;
    const featuredMediaId = this.dataset.variantFeaturedMediaId;

    /**
     * @param {HTMLElement} item
     * @returns {boolean}
     */
    const matchesColor = (item) => {
      const img = item.querySelector('img');
      const alt = img?.alt?.trim().toLowerCase() ?? '';
      if (alt === normalizedColor) return true;

      if (!featuredMediaId) return false;

      const mediaEl = item.querySelector(`[data-media-id="${featuredMediaId}"]`);
      return mediaEl instanceof HTMLElement;
    };

    /**
     * @param {NodeListOf<Element> | Element[]} elements
     * @returns {number}
     */
    const filterElements = (elements) => {
      let visibleCount = 0;

      elements.forEach((element) => {
        if (!(element instanceof HTMLElement)) return;

        const isMatch = matchesColor(element);
        element.toggleAttribute('hidden', !isMatch);
        element.classList.toggle('media-gallery__item--color-hidden', !isMatch);

        if (isMatch) visibleCount += 1;
      });

      return visibleCount;
    };

    const slides = this.querySelectorAll('slideshow-slide');
    let visibleCount = filterElements(slides);
    visibleCount += filterElements(this.querySelectorAll('.media-gallery__grid > li'));
    visibleCount += filterElements(this.querySelectorAll('.slideshow-controls__thumbnail'));
    visibleCount += filterElements(this.querySelectorAll('.dialog-thumbnails-list__thumbnail'));
    visibleCount += filterElements(this.querySelectorAll('.dialog-zoomed-gallery > li'));

    if (visibleCount === 0 && featuredMediaId) {
      /**
       * @param {NodeListOf<Element> | Element[]} elements
       */
      const showFeaturedOnly = (elements) => {
        elements.forEach((element) => {
          if (!(element instanceof HTMLElement)) return;

          const isFeatured = Boolean(element.querySelector(`[data-media-id="${featuredMediaId}"]`));
          element.toggleAttribute('hidden', !isFeatured);
          element.classList.toggle('media-gallery__item--color-hidden', !isFeatured);
        });
      };

      showFeaturedOnly(slides);
      showFeaturedOnly(this.querySelectorAll('.media-gallery__grid > li'));
      showFeaturedOnly(this.querySelectorAll('.slideshow-controls__thumbnail'));
      showFeaturedOnly(this.querySelectorAll('.dialog-thumbnails-list__thumbnail'));
      showFeaturedOnly(this.querySelectorAll('.dialog-zoomed-gallery > li'));
    }

    let firstVisibleSlideIndex = -1;
    slides.forEach((slide, index) => {
      if (!(slide instanceof HTMLElement) || slide.hasAttribute('hidden')) return;
      if (firstVisibleSlideIndex === -1) {
        firstVisibleSlideIndex = index;
      }
    });

    if (firstVisibleSlideIndex >= 0) {
      const index = firstVisibleSlideIndex;
      requestAnimationFrame(() => {
        this.slideshow?.select(index, undefined, { animate: false });
      });
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
